import {Injectable} from "@angular/core";
import {catchError, forkJoin, Observable, of, ReplaySubject, Subject, switchMap, tap, throwError} from "rxjs";
import {AuthorizationServiceProvider} from "./authorization-service.provider";
import {NativeServiceApiProvider} from "../../services/native-api-provider.service";
import {LogsService} from "../../utils/services/logs.service";
import {V3Person} from "../model/v3/people";
import {V3School} from "../model/v3/schools";
import {V3FileItem} from "../model/v3/file";

@Injectable({
  providedIn: 'root'
})
export class ProfileService {
  private _selfPerson: Subject<V3Person>;
  private _selfPhoto: Subject<V3FileItem>;

  constructor(
    private provider: NativeServiceApiProvider,
    private authService: AuthorizationServiceProvider,
    private logs: LogsService) {
  }

  /**
   * getting the profile you may subscribe for updates too
   */
  getSelfPerson(): Observable<V3Person> {
    // if not initialized yet - load data
    if (!this._selfPerson) {
      this._selfPerson = new ReplaySubject<V3Person>(1);
      this.reloadSelfPerson().subscribe(person => {
        this._selfPerson.next(person)
      });
    }
    return this._selfPerson;
  }

  /**
   * may be used to reload profile data
   */
  reloadSelfPerson(): Observable<V3Person> {
    if (!this._selfPerson) {
      this._selfPerson = new ReplaySubject<V3Person>(1);
    }
    return forkJoin([
      this.provider.casa(),
      this.getSelfPersonId()
    ]).pipe(
      switchMap(([api, personId]) => api.getPerson(personId)),
      tap(person => this._selfPerson.next(person)),
      catchError(err => {
        this.logs.error("got error when asking for self person", err)
        return throwError(() => err);
      })
    )
  }

  getSelfSchool(): Observable<V3School> {
    return this.authService.getAuthDetailsService()
      .pipe(
        switchMap(auth => auth.getSelfSchoolId()),
        switchMap(schoolId =>
          this.provider.casa().pipe(
            switchMap(casaApi => casaApi.getSchool(schoolId))
          )
        )
      );
  }

  getSelfPhoto(): Observable<V3FileItem> {
    if (!this._selfPhoto) {
      this._selfPhoto = new ReplaySubject<V3FileItem>(1);
      this.reloadSelfPhoto().subscribe(photo => {
        this._selfPhoto.next(photo)
      });
    }
    return this._selfPhoto;
  }

  reloadSelfPhoto(): Observable<V3FileItem> {
    if (!this._selfPhoto) {
      this._selfPhoto = new ReplaySubject<V3FileItem>(1);
    }
    return forkJoin([
      this.provider.casa(),
      this.getSelfPersonId()
    ]).pipe(
      switchMap(([api, personId]) => api.getPhoto(personId)),
      tap(photo => this._selfPhoto.next(photo)),
      catchError(() => {
        this._selfPhoto.next(null);
        return of(null)
      })
    )
  }

  private getSelfPersonId() {
    return this.authService.getAuthDetailsService().pipe(
      switchMap(auth => auth.getSelfPersonId()),
    )
  }
}
